import type { EntityIntegrations } from '../types/order.types';

/**
 * Pure readings of an entity-service `Entity` document. Two callers share them
 * so they cannot disagree: the account switch (`switchEntity`, which writes the
 * scope) and the internal photo workspace (`useWorkspaceAccount`, which reads a
 * property's account without switching into it). No store import on purpose.
 */

/**
 * Watermark signal from `watermarkImage`. An absent key means "no signal"
 * (null). An explicit null or a blank string means "no watermark on file"
 * (false). Only an explicit `false` ever disables the watermark toggle
 * downstream, so an absent signal is never over-restrictive.
 *
 * That the two cases differ makes this depend on entity-service serializing the
 * key rather than omitting it — see the wire contract on
 * `EntityCheckoutInfo.watermarkImage` before changing either side.
 */
export const watermarkSignal = (
  doc: { watermarkImage?: string | null } | undefined
): boolean | null => {
  if (!doc || !('watermarkImage' in doc)) return null;
  return (
    typeof doc.watermarkImage === 'string' &&
    doc.watermarkImage.trim().length > 0
  );
};

/**
 * Whether the account has a custom webhook endpoint, which is what the Send
 * gate has always asked. Legacy `integration_webhooks` do not count.
 */
export const hasWebhookEndpoints = (
  integrations: EntityIntegrations | null | undefined
): boolean => {
  const webhooks = integrations?.custom_integration_webhooks;
  return Array.isArray(webhooks) && webhooks.length >= 1;
};
